/**
 * Canada's NANP area codes. Canada shares the +1 country code with the US, so
 * the dialling prefix cannot tell them apart and only the area code can.
 */
const CANADA_AREA_CODES = new Set([
  "204", "226", "236", "249", "250", "257", "263", "289", "306", "343", "354", "365",
  "367", "368", "382", "387", "403", "416", "418", "428", "431", "437", "438",
  "450", "468", "474", "506", "514", "519", "548", "579", "581", "584", "587",
  "600", "604", "613", "639", "647", "672", "683", "705", "709", "742", "753",
  "778", "780", "782", "807", "819", "825", "867", "873", "879", "902", "905",
  "942",
])

/**
 * Sovereign foreign countries that also dial +1, mostly Caribbean. These are
 * INTERNATIONAL to a carrier even though they look domestic.
 *
 * US territories are deliberately absent: Puerto Rico (787/939), the US Virgin
 * Islands (340), Guam (671), American Samoa (684) and the Northern Marianas
 * (670) are US domestic for messaging, so they fall through to "US".
 */
const FOREIGN_NANP_AREA_CODES = new Set([
  "242", "246", "264", "268", "284", "345", "441", "473", "649", "658", "664",
  "721", "758", "767", "784", "809", "829", "849", "868", "869", "876",
])

/**
 * Which of Quo's three restriction regions a destination number belongs to.
 *
 * Quo scores "US", "CA" and "Intl" SEPARATELY and they genuinely differ: while
 * A2P registration is pending, US messaging is restricted and Canadian
 * messaging is not. So testing `startsWith("+1")` and calling the result "US"
 * silently skips every Canadian reminder we are perfectly able to send, which
 * is invisible from the outside: the send is a named skip, not an error, and
 * the row just never gets a text. We coach men in Toronto and Montreal, so this
 * is a real lane rather than a hypothetical one.
 *
 * A +1 number that is neither US nor Canadian is a Caribbean NANP country
 * (Jamaica, the Bahamas, and so on), which is "Intl" to Quo rather than
 * domestic. Erring that way is also the safer of the two mistakes: an
 * over-restrictive call skips a send we could have made, while an
 * under-restrictive one attempts a send Quo refuses.
 */
export function quoRegionFor(to: string): "US" | "CA" | "Intl" {
  const digits = to.replace(/\D/g, "")
  if (!digits.startsWith("1") || digits.length !== 11) return "Intl"
  const areaCode = digits.slice(1, 4)
  if (CANADA_AREA_CODES.has(areaCode)) return "CA"
  if (FOREIGN_NANP_AREA_CODES.has(areaCode)) return "Intl"
  return "US"
}

/** Quo's international-rate table prices these US territories as paid calls.
 * They remain US for SMS restrictions above, so application voice routing
 * must keep its own policy rather than changing the SMS classifier. */
const CHARGED_US_TERRITORY_AREA_CODES = new Set([
  "340", "670", "671", "684", "787", "939",
])

/** Select the manual voice channel for an application call. Ordinary US and
 * Canadian destinations use Quo's included calling; international numbers and
 * charged US territories use WhatsApp Business App voice. */
export function applicationVoiceChannelFor(normalizedE164: string): "quo" | "whatsapp" {
  const digits = normalizedE164.replace(/\D/g, "")
  if (
    digits.startsWith("1") &&
    digits.length === 11 &&
    CHARGED_US_TERRITORY_AREA_CODES.has(digits.slice(1, 4))
  ) {
    return "whatsapp"
  }
  return quoRegionFor(normalizedE164) === "Intl" ? "whatsapp" : "quo"
}
